import type { ReactNode } from 'react';

import type { OptionType } from '@atlaskit/select/types';

import type {
	AdfValue,
	DateTimeValue,
	DateValue,
	FilesValue,
	FormOptionValue,
	IsoDate,
	LocalTime,
	LocalTimeValue,
	NumberValue,
	OptionsValue,
	TextValue,
} from './field-values';

export interface FormFieldValidation {
	maximumCharacters?: number;
	maximumWords?: number;
	maximumNumber?: number;
	maximumDate?: IsoDate;
	maximumTime?: LocalTime;
	maximumOptionSelections?: number;
	minimumCharacters?: number;
	minimumWords?: number;
	minimumNumber?: number;
	minimumDate?: IsoDate;
	minimumTime?: LocalTime;
	minimumOptionSelections?: number;
	/** Regex format validation */
	regexValidation?: {
		/** Regex pattern */
		pattern: string | RegExp;
		/** Message to display to user if the answer doesn't match the expected format */
		mismatchMessage?: string;
	};
	required?: boolean;
	requiredOption?: FormOptionValue;
	wholeNumbersOnly?: boolean;
}

// oxlint-disable-next-line eslint/no-redeclare
export const FormFieldType: {
	readonly ATTACHMENT: 'attachment';
	readonly CASCADING_SELECT: 'cascadingSelect';
	readonly SINGLE_SELECT: 'singleSelect';
	readonly MULTIPLE_SELECT: 'multipleSelect';
	readonly CHECKBOX: 'checkbox';
	readonly RADIO: 'radio';
	readonly RATING: 'rating';
	readonly SCALE: 'scale';
	readonly DATE: 'date';
	readonly DATE_TIME: 'dateTime';
	readonly LOCAL_TIME: 'localTime';
	readonly NUMBER: 'number';
	readonly RICH_TEXT_PARAGRAPH: 'richTextParagraph';
	readonly EMAIL: 'email';
	readonly LONG_TEXT: 'longText';
	readonly PARAGRAPH: 'paragraph';
	readonly SHORT_TEXT: 'shortText';
	readonly URL: 'url';
	readonly CUSTOM: 'custom';
} = {
	ATTACHMENT: 'attachment',
	CASCADING_SELECT: 'cascadingSelect',
	SINGLE_SELECT: 'singleSelect',
	MULTIPLE_SELECT: 'multipleSelect',
	CHECKBOX: 'checkbox',
	RADIO: 'radio',
	RATING: 'rating',
	SCALE: 'scale',
	DATE: 'date',
	DATE_TIME: 'dateTime',
	LOCAL_TIME: 'localTime',
	NUMBER: 'number',
	RICH_TEXT_PARAGRAPH: 'richTextParagraph',
	EMAIL: 'email',
	LONG_TEXT: 'longText',
	PARAGRAPH: 'paragraph',
	SHORT_TEXT: 'shortText',
	URL: 'url',
	CUSTOM: 'custom',
} as const;

export type FormFieldType = (typeof FormFieldType)[keyof typeof FormFieldType];

export interface Avatar {
	id: string;
	url16: string;
	url48: string;
}

/**
 * Type definitions for a field. Depending on the `type` of a field, it will be mapped to a matching `value` type.
 *
 * Use utility methods in `utils.ts` to cast a generic `FormField` type into a specific field type to provide
 * appropriate typing for `value`.
 */

export interface FormAdfField {
	helperMessage?: string;
	label: string;
	type: typeof FormFieldType.RICH_TEXT_PARAGRAPH;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[typeof FormFieldType.RICH_TEXT_PARAGRAPH];
}

export interface FormAttachmentField {
	helperMessage?: string;
	label: string;
	type: typeof FormFieldType.ATTACHMENT;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[typeof FormFieldType.ATTACHMENT];
}

export interface FormDateField {
	helperMessage?: string;
	label: string;
	type: typeof FormFieldType.DATE;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[typeof FormFieldType.DATE];
}

export interface FormDateTimeField {
	helperMessage?: string;
	label: string;
	type: typeof FormFieldType.DATE_TIME;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[typeof FormFieldType.DATE_TIME];
}

export interface FormLocalTimeField {
	helperMessage?: string;
	label: string;
	type: typeof FormFieldType.LOCAL_TIME;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[typeof FormFieldType.LOCAL_TIME];
}

export interface FormNumberField {
	helperMessage?: string;
	label: string;
	type: typeof FormFieldType.NUMBER;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[typeof FormFieldType.NUMBER];
}

export interface FormOption {
	id: OptionId;
	label: string;
	children?: FormOption[];
	avatar?: ReactNode;
	byline?: string;
}

export const FORM_OPTION_FIELD_TYPES: readonly [
	'cascadingSelect',
	'singleSelect',
	'multipleSelect',
	'checkbox',
	'radio',
] = [
	FormFieldType.CASCADING_SELECT,
	FormFieldType.SINGLE_SELECT,
	FormFieldType.MULTIPLE_SELECT,
	FormFieldType.CHECKBOX,
	FormFieldType.RADIO,
] as const;

export type FormOptionFieldType = (typeof FORM_OPTION_FIELD_TYPES)[number];

export interface FormOptionField {
	options: FormOption[];
	helperMessage?: string;
	hasOtherOption?: boolean;
	label: string;
	type: FormOptionFieldType;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[FormOptionFieldType];
}

export interface FormOptionsType extends OptionType {
	avatar?: ReactNode;
	byline?: string;
	/**
	 * @deprecated Temporary workaround to fix CMDB field menu item display behavior.
	 * Follow-up work to replace this prop with a proper solution can be tracked in REQIN-4835.
	 */
	displayLabel?: string;
}

export const FORM_TEXT_FIELD_TYPES: readonly [
	'email',
	'longText',
	'paragraph',
	'shortText',
	'url',
] = [
	FormFieldType.EMAIL,
	FormFieldType.LONG_TEXT,
	FormFieldType.PARAGRAPH,
	FormFieldType.SHORT_TEXT,
	FormFieldType.URL,
] as const;

export type FormTextFieldType = (typeof FORM_TEXT_FIELD_TYPES)[number];

export interface FormTextField {
	helperMessage?: string;
	label: string;
	type: FormTextFieldType;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[FormTextFieldType];
}

export const FORM_RANKING_FIELD_TYPES: readonly ['rating', 'scale'] = [
	FormFieldType.RATING,
	FormFieldType.SCALE,
] as const;

export type FormRankingFieldType = (typeof FORM_RANKING_FIELD_TYPES)[number];

export interface FormRankingField {
	options: FormOption[];
	helperMessage?: string;
	label: string;
	type: FormRankingFieldType;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[FormRankingFieldType];
}

/**
 * Generic definition for the API
 */
export interface FormCustomField {
	type: typeof FormFieldType.CUSTOM;
	label: string;
	helperMessage?: string;
	validation: FormFieldValidation;
	value?: FormFieldTypeToValueType[typeof FormFieldType.CUSTOM];
}

export type FormField =
	| FormAdfField
	| FormAttachmentField
	| FormDateField
	| FormDateTimeField
	| FormLocalTimeField
	| FormNumberField
	| FormOptionField
	| FormTextField
	| FormRankingField
	| FormCustomField;

export interface FormFields {
	readonly [fieldId: FieldId]: FormField;
}

/**
 * The ID of a field.
 */
export type FieldId = string;

/**
 * The ID of an option.
 *
 * When this is used for a flattened cascading option the format will be `parentId:childId`.
 */
export type OptionId = string;

export type FormFieldTypeToValueType = {
	[OptionFieldType in FormOptionFieldType]: OptionsValue;
} & {
	[TextFieldType in FormTextFieldType]: TextValue;
} & {
	[RankingFieldType in FormRankingFieldType]: NumberValue;
} & {
	[FormFieldType.NUMBER]: NumberValue;
	[FormFieldType.DATE]: DateValue;
	[FormFieldType.DATE_TIME]: DateTimeValue;
	[FormFieldType.LOCAL_TIME]: LocalTimeValue;
	[FormFieldType.ATTACHMENT]: FilesValue;
	[FormFieldType.RICH_TEXT_PARAGRAPH]: AdfValue;
	[FormFieldType.CUSTOM]: TextValue;
};
